import type { Node, Parent } from 'unist';

/**
 * Continue traversing as normal
 */
export const CONTINUE = true;
/**
 * Do not traverse this node’s children
 */
export const SKIP = 'skip';
/**
 * Stop traversing immediately
 */
export const EXIT = false;

type Action = boolean | 'skip';
type Index = number;
type ActionTuple = [(Action | null | undefined | void)?, (Index | null | undefined)?];
type VisitorResult = null | undefined | Action | Index | ActionTuple | void;

/**
 * Visit children of tree which pass a test
 */
export async function visitAsync<T>(
    tree: Node,
    test:
        | ((node: Node, index: number | null, parent: Parent | null) => node is T & Node)
        | ((node: Node, index: number | null, parent: Parent | null) => boolean),
    visitor: (
        node: Node & T,
        index: number | null,
        parent: Parent
    ) => VisitorResult | Promise<VisitorResult>,
    reverse = false
) {
    const step = reverse ? -1 : 1;

    function factory(node: Node, index: number | null, parents: Parent[]) {
        return async function visit() {
            let result: ActionTuple = [];
            let subresult;
            let offset;
            let grandparents;

            if (test(node, index, parents[parents.length - 1] || null)) {
                const parent = parents[parents.length - 1];
                result = toResult(
                    // @ts-expect-error
                    await visitor(node, parent ? parent.children.indexOf(node) : null, parent)
                );

                if (result[0] === EXIT) {
                    return result;
                }
            }

            // @ts-expect-error
            const children = node.children;
            if (children && result[0] !== SKIP) {
                offset = (reverse ? children.length : -1) + step;
                // @ts-expect-error
                grandparents = parents.concat(node);

                while (offset > -1 && offset < children.length) {
                    subresult = await factory(children[offset], offset, grandparents)();

                    if (subresult[0] === EXIT) {
                        return subresult;
                    }

                    offset = typeof subresult[1] === 'number' ? subresult[1] : offset + step;
                }
            }

            return result;
        };
    }

    await factory(tree, null, [])();
    return;
}

function toResult(value: VisitorResult): ActionTuple {
    if (Array.isArray(value)) {
        return value;
    }

    if (typeof value === 'number') {
        return [CONTINUE, value];
    }

    return [value];
}
